import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  Snackbar,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LockIcon from '@mui/icons-material/Lock';
import CircleIcon from '@mui/icons-material/Circle';
import { useNavigate } from 'react-router-dom';
import { ROUTE_THING, ROUTE_THING_CREATEEDIT, THING_ID } from '../../constants';
import '../../style/Thing.css';
import OutlinedButton from '../../components/OutlinedButton';
import useThingStore from '../../stores/ThingStore';
import ThingApi from '../../api/ThingApi';
import ProjectApi from '../../api/ProjectApi';
import { getLocalStorage, useError } from '../../utils/functions';
import DialogBox from '../../components/DialogBox';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ThingInformation: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [projectName, setProjectName] = useState('');
  const { thingObj, setThingObj } = useThingStore();
  const [delOpen, setDelOpen] = useState(false);

  const [topic, setTopic] = useState({
    t1: '',
    t2: '',
    t3: '',
    t4: '',
    t5: '',
    t6: '',
    t7: '',
    t8: '',
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleDelete = async () => {
    if (getLocalStorage(THING_ID)) {
      const res = await ThingApi.deleteThing(getLocalStorage(THING_ID));
      if (res.code) {
        setError(res.message);
      } else {
        setDelOpen(false);
        navigate(ROUTE_THING);
      }
    }
  };

  const copyToClipboard = (text: any) => {
    if (!text) return;
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textArea);
  };

  const queryThing = async () => {
    const thingId = getLocalStorage(THING_ID);
    const res = await ThingApi.getThingById(thingId);

    if (res.code) {
      setError(res.message);
    } else {
      res.data.clientIdShow = 'thing:' + res.data.id;
      setThingObj(res.data);

      const projectId = res.data.projectId ? res.data.projectId : '$projectId';

      setTopic({
        t1: '$thing/' + projectId + '/' + thingId + '/$conf/$expected/(${configName}|#)',
        t2: '$thing/' + projectId + '/' + thingId + '/$cmd/$downlink/${commandName}',
        t3: '$thing/' + projectId + '/' + thingId + '/$conf/$reported/(${configName}|#)',
        t4: '$thing/' + projectId + '/' + thingId + '/$data/(${dataBucketName}|#)',
        t5: '$thing/' + projectId + '/' + thingId + '/$alert',
        t6: '$thing/' + projectId + '/' + thingId + '/$conf/$reported/${configName}',
        t7: '$thing/' + projectId + '/' + thingId + '/$cmd/$uplink',
        t8: '$thing/' + projectId + '/' + thingId + '/$data/${dataBucketName}',
      });


      const proRes = await ProjectApi.getProject(res.data.projectId);
      if (proRes !== 'error') {
        setProjectName(proRes.name);
      }
    }
  }

  useEffect(() => {
    queryThing();
  }, []);

  const statusColor = thingObj.status === 'active'
    ? 'success'
    : thingObj.status === 'transferring'
      ? 'warning'
      : 'inherit';

  const statusClass = thingObj.isOnline ? 'thing-color-active' : 'thing-color-default';
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mb={4}>
          <Box display="flex" gap={2} alignItems="center">
            <CircleIcon
              fontSize="small"
              className={`${statusClass}`}
            />
            <Typography variant="h4" gutterBottom className='primaryBlueColor'>{thingObj.name}</Typography>
            <Button variant="contained" color={statusColor} sx={{ mr: 1, height: '20px', 'pointer-events': 'none' }} size='small'>{thingObj.status}</Button>
          </Box>
          <Box display="flex" gap={2}>
            <Typography color="textSecondary">Last command: {thingObj.lastCmdTime}</Typography>
            <Typography color="textSecondary">Last data: {thingObj.lastDataTime}</Typography>
          </Box>
        </Box>
        <Box>
          <OutlinedButton
            onClick={() => console.log('Clone')}
            icon={<ContentCopyIcon />}
            text='Clone' />

          <OutlinedButton
            onClick={() => setDelOpen(true)}
            icon={<DeleteOutlineOutlinedIcon />}
            text='Delete' />

          <OutlinedButton
            onClick={() => navigate(ROUTE_THING_CREATEEDIT)}
            icon={<EditOutlinedIcon />}
            text='Edit' />
        </Box>
      </Box>

      <Tabs
        value={value}
        onChange={handleChange}
      >
        <Tab label="Basic" {...a11yProps(0)} />
        <Tab label="Topic" {...a11yProps(1)} />
      </Tabs>

      <CustomTabPanel value={value} index={0}>
        <Box mb={4} display="flex" gap={4}>
          <Box flex={1}>
            <Typography className='text_gray'>ID/SECRET</Typography>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Identity</Typography>
              <TextField
                variant="outlined"
                value={thingObj?.id || ""}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(thingObj.id)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Secret</Typography>
              <TextField
                variant="outlined"
                value={thingObj?.secret || ""}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(thingObj.secret)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Client ID</Typography>
              <TextField
                variant="outlined"
                value={thingObj?.clientIdShow || ""}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(thingObj.clientIdShow)} />
            </Box>
          </Box>
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <Box mb={4} display="flex" gap={4}>
          <Box flex={1}>
            <Typography className='text_gray'>THING---SUBSCRIBE</Typography>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Config topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t1}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t1)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Command topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t2}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t2)} />
            </Box>
            <Box className='thing_info_box1'></Box>
            <Box className='thing_info_box1'></Box>
            <Typography className='text_gray'>APP---SUBSCRIBE</Typography>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Config topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t3}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t3)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Data topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t4}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t4)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Alert topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t5}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t5)} />
            </Box>
          </Box>

          <Box flex={1}>
            <Typography className='text_gray'>THING---PUBLISH</Typography>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Config ack topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t6}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t6)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Command ack topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t7}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t7)} />
            </Box>
            <Box className='thing_info_box1'>
              <Typography className='w150'>Data topic</Typography>
              <TextField
                variant="outlined"
                value={topic.t8}
                size="small"
                disabled={true}
                className='w430'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(topic.t8)} />
            </Box>
          </Box>
        </Box>
      </CustomTabPanel>

      <Box mb={4} display="flex" gap={4}>
        <Box flex={1}>
          <Typography variant="h5">Information</Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>{thingObj.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Serial number</TableCell>
                  <TableCell>{thingObj.serialNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Model name</TableCell>
                  <TableCell>{thingObj.modelName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell>{thingObj.description}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Thing type</TableCell>
                  <TableCell>{thingObj.isEon ? "EoN" : "Device"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Visibility</TableCell>
                  <TableCell>
                    {thingObj.visibility === 'public' ? (
                      <>
                        {thingObj.visibility} < LanguageIcon />
                      </>

                    ) : (
                      <>
                        {thingObj.visibility} < LockIcon />
                      </>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>
                    [{thingObj.coordinates?.join(", ") || "N/A"}]
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Created by</TableCell>
                  <TableCell></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Created at</TableCell>
                  <TableCell>{thingObj.createdAt}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Connected Proj</TableCell>
                  <TableCell>
                    {projectName && (
                      <Chip
                        key={projectName}
                        label={projectName}
                        sx={{ marginRight: "5px", backgroundColor: "#E8EAF6", color: "#1A237E" }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Label Section */}
        <Box flex={1}>
          <Typography variant="h6">Label</Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f0f0f0' }}>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {thingObj.labels?.map((label) => (
                  <TableRow key={label.key}>
                    <TableCell>{label.key}</TableCell>
                    <TableCell>{label.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        message="Copied!"
        onClose={() => setOpenSnackbar(false)}
      />
      <DialogBox
        open={delOpen}
        title='Delete'
        dialogContent={
          <>
            {thingObj.name} will be permanently deleted.
          </>
        }
        showCancel={true}
        onCancel={() => setDelOpen(false)}
        onConfirm={handleDelete}
        isError={true}
        confirmLabel='Delete'
      />
    </Box >
  );
};

export default ThingInformation;
